import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  getCompanyUploadedFileSignedUrl,
  previewAllPaymentPlatformFiles,
  registerPaymentPlatformFile,
  startPaymentPlatformImport,
} from "../api/companyApi";
import PaymentPlatformColumnMappingDialog from "./PaymentPlatformColumnMappingDialog";
import PaymentPlatformFileUploadDialog from "./PaymentPlatformFileUploadDialog";

const PaymentPlatformQuickUploadFlow = forwardRef(function PaymentPlatformQuickUploadFlow(
  { tenantId },
  ref,
) {
  const fileInput = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [pendingImport, setPendingImport] = useState(null);
  const [busy, setBusy] = useState(false);

  const open = useCallback(() => {
    if (!tenantId || busy) return;
    if (fileInput.current) fileInput.current.value = "";
    fileInput.current?.click();
  }, [tenantId, busy]);

  useImperativeHandle(ref, () => ({ open }), [open]);

  const chooseFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length) setSelectedFiles(files);
  };

  const uploadFiles = async (labelledFiles) => {
    setBusy(true);
    try {
      for (const { file, labels } of labelledFiles) {
        const signed = await getCompanyUploadedFileSignedUrl(tenantId, {
          fileName: file.name,
          fileType: file.type || "application/octet-stream",
        });
        await axios.put(signed.data.uploadUrl, file, {
          headers: { "Content-Type": file.type || "application/octet-stream" },
        });
        await registerPaymentPlatformFile(tenantId, {
          fileGCSKey: signed.data.fileGCSKey,
          fileName: signed.data.originalFileName || file.name,
          labels,
          source: "dashboard_upload",
        });
      }
      const preview = await previewAllPaymentPlatformFiles(tenantId);
      setPendingImport({ files: preview.files, preview });
      setSelectedFiles([]);
      toast.success("Files added. Map columns to finish the import.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "File upload failed");
    } finally {
      setBusy(false);
    }
  };

  const applyMapping = async (columnMapping) => {
    if (!pendingImport?.files?.length) return;
    setBusy(true);
    try {
      const job = await startPaymentPlatformImport(
        tenantId,
        pendingImport.files,
        null,
        columnMapping,
      );
      setPendingImport(null);
      window.dispatchEvent(new CustomEvent("payment-platform-import-started", {
        detail: { tenantId, job },
      }));
      toast.success("Import started. You can continue working on this page.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not start import");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        hidden
        multiple
        ref={fileInput}
        aria-label="Choose payment platform data files"
        type="file"
        accept=".csv,.xls,.xlsx"
        onChange={chooseFiles}
      />
      <PaymentPlatformFileUploadDialog
        open={selectedFiles.length > 0}
        files={selectedFiles}
        busy={busy}
        onClose={() => setSelectedFiles([])}
        onUpload={uploadFiles}
      />
      <PaymentPlatformColumnMappingDialog
        open={Boolean(pendingImport)}
        preview={pendingImport?.preview}
        currentMapping={null}
        busy={busy}
        onClose={() => setPendingImport(null)}
        onApply={applyMapping}
        onSaveDraft={null}
      />
    </>
  );
});

export default PaymentPlatformQuickUploadFlow;
